import qs from 'query-string';
import { useLocation } from 'react-router';
import { has, get, forEach, pull } from 'lodash';

import React, {
  useRef,
  useReducer,
  useMemo,
  useContext,
  useState,
} from 'react';

import { createRestApi } from '~/App/Api/rest';
import StepNote from './TodoCreate/Steps/Note';
import Typeform from '~/App/Components/Typeform';
import StepNames from './TodoCreate/Steps/Names';
import StepSendTo from './TodoCreate/Steps/SendTo';
import StepRollUp from './TodoCreate/Steps/RollUp';
import WorkspaceContext from '~/App/Contexts/WorkspaceContext';
import StepConfirm from './TodoCreate/Steps/Confirm';
import StepAssignee from './TodoCreate/Steps/Assignee';
import { getNameFromEmail } from '~/BonjorosList/util';
import reducer, { defaultState } from './TodoCreate/reducer';
import FullscreenModal from '~/App/Components/Modals/Fullscreen';
import useRollUpState from '~/App/Modules/TodoList/use-roll-up-state';
import type { Greet, Profile, UserSimple } from '~/types/models';
import type { Contacts } from '~/types/redux/todo';
import useCurrentUser from '~/react-hooks/use-current-user';
import { createProfile } from '~/App/Api/Profile';

export enum TodoCreateMode {
  SINGLE = 'single',
  GROUP = 'group',
  ROLLUP = 'rollup',
}

const TodoCreateModes = {
  [TodoCreateMode.SINGLE]: TodoCreateMode.SINGLE,
  [TodoCreateMode.GROUP]: TodoCreateMode.GROUP,
  [TodoCreateMode.ROLLUP]: TodoCreateMode.ROLLUP,
};

const TodoCreateHeaderText = {
  [TodoCreateMode.SINGLE]: 'Send a Bonjoro message to a contact',
  [TodoCreateMode.GROUP]: 'Send a Bonjoro message to a group',
  [TodoCreateMode.ROLLUP]: 'Roll Up Existing To-Dos',
};

export interface TodoCreateQueryString {
  mode: TodoCreateMode;
}

export type SelectContactEvent = Event & {
  data?: {
    maxContacts: number;
    contactCount: number;
  };
};

function TodoCreate() {
  const location = useLocation();
  const greetsApi = useMemo(() => createRestApi<Greet>('/api/v2/greets'), []);

  const { rollUp, setRollUp } = useRollUpState();
  const containerRef = useRef();
  const { features } = useCurrentUser();
  const { selectedWorkspace } = useContext(WorkspaceContext);

  const TodoCreateMaxContacts = {
    [TodoCreateMode.SINGLE]: 1,
    [TodoCreateMode.GROUP]: 5,
    [TodoCreateMode.ROLLUP]: features.greetRollUpLimit as number,
  };

  const mode = useMemo(() => {
    const queryString = qs.parse(
      location.search
    ) as unknown as TodoCreateQueryString;

    return get(TodoCreateModes, queryString.mode) || TodoCreateMode.SINGLE;
  }, [location.search]);

  const headingText = TodoCreateHeaderText[mode];
  const maxContacts = TodoCreateMaxContacts[mode];

  const [state, dispatch] = useReducer(reducer, defaultState);
  const [unsubscribedContact, setUnsubscribedContact] = useState<
    Profile | false
  >(false);

  const actions = useMemo(
    () => ({
      deselectContact: (key: string, label: string = key, data: string = key) =>
        dispatch({
          type: 'deselectContact',
          payload: {
            key,
            label,
            data,
          },
        }),

      updateContact: (key: string, column: string, value: string) =>
        dispatch({
          type: 'updateContact',
          payload: {
            key,
            column,
            value,
          },
        }),

      updateNote: (note?: string) =>
        dispatch({
          type: 'updateNote',
          payload: note,
        }),

      updateAssignee: (assignee: UserSimple) =>
        dispatch({
          type: 'updateAssignee',
          payload: assignee,
        }),

      addGreet: (greet: Greet) =>
        dispatch({
          type: 'addGreet',
          payload: {
            key: greet.id,
            data: greet,
          },
        }),

      removeGreet: (greetId: string) =>
        dispatch({
          type: 'removeGreet',
          payload: {
            key: greetId,
          },
        }),
    }),
    []
  );

  const contactCount = useMemo(() => state.get('contacts').count(), [state]);
  const contacts = useMemo(
    () => state.get('contacts').toJS() as Record<string, Contacts>,
    [state]
  );
  const contactsArray = useMemo(() => Object.values(contacts), [contacts]);
  const contactsAsProfiles = useMemo(() => {
    return contactsArray.map((contact) => {
      // is a profile
      if (has(contact, 'data.id')) {
        return get(contact, 'data');
      }

      // is just an email
      const name = getNameFromEmail(get(contact, 'data'));
      const nameSegments = name.split(' ');

      return {
        first_name: get(nameSegments, 0),
        last_name: get(nameSegments, 1),
        email: get(contact, 'data'),
      };
    });
  }, [contactsArray]);

  const sharedProps = useMemo(() => {
    function selectContact(
      key: string,
      label: string | null = key,
      data: Profile
    ) {
      if (maxContacts <= contactCount) {
        return;
      }

      dispatch({
        type: 'selectContact',
        payload: {
          key,
          label,
          data,
        },
      });

      const event: SelectContactEvent = new Event('todocreate::selectContact');

      event.data = {
        maxContacts: maxContacts ?? 0,
        contactCount: contactCount + 1,
      };

      window.dispatchEvent(event);
    }

    return {
      mode,
      maxContacts,
      contactCount,
      hasReachedMaxContacts: contactCount >= maxContacts,

      contacts,
      contactsArray,
      unsubscribedContact,
      contactsAsProfiles,

      contactUpdates: state.get('updates').toJS(),

      selectContact,

      selectEmail: async function (email: string) {
        const profile = await createProfile({ email });

        if (!profile.unsubscribed) {
          setUnsubscribedContact(false);
          selectContact(profile.id, profile.name, profile);
        } else {
          setUnsubscribedContact(profile);
        }
      },

      selectGreet: async function (greet: string | Greet) {
        if (typeof greet === 'string') {
          greet = await greetsApi.get(greet);
        }

        actions.addGreet(greet);

        forEach(greet.profiles, (profile) =>
          selectContact(profile.id, profile.name, profile)
        );
      },

      deselectGreet: function (greetId: string) {
        actions.removeGreet(greetId);
        setRollUp(pull(rollUp, greetId));
      },

      note: state.get('note'),
      assignee: state.get('assignee'),

      ...actions,

      isContactSelected: (key: string) => state.hasIn(['contacts', key]),

      greets: state.get('greets').toArray(),
    };
  }, [state, unsubscribedContact]);

  const StepOne = mode == TodoCreateMode.ROLLUP ? StepRollUp : StepSendTo;

  return (
    <FullscreenModal heading={headingText} containerRef={containerRef}>
      <Typeform className='max-w-[640px]' childClassName='flex-col'>
        <StepOne {...sharedProps} />
        <StepNames {...sharedProps} />
        <StepNote {...sharedProps} />
        <StepAssignee {...sharedProps} />
        <StepConfirm {...sharedProps} workspace={selectedWorkspace} />
      </Typeform>
    </FullscreenModal>
  );
}

export default TodoCreate;
